import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import CircularProgress from "@/components/common/CircularProgress";
import { checkComunidadAdminAccess, comunidadAdapter } from "@/hooks/api/comunidad";
import { hasPendingLocalDrafts } from "./useLocalDraft";
import { toastUtils } from "@/hooks/useToast";
import { NewsFeedEditor } from "./components/NewsFeedEditor";
import { NewsletterEditionsEditor } from "./components/NewsletterEditionsEditor";
import { CalendarEventsEditor } from "./components/CalendarEventsEditor";
import { FormacionInscriptosPanel } from "./components/FormacionInscriptosPanel";
import { VocesDeLaRedPanel } from "./components/VocesDeLaRedPanel";

/**
 * Panel de admin de contenido de Comunidad. El chequeo de acceso de acá es solo para no
 * mostrar la pantalla en blanco un instante o dejar a alguien mirando un panel que no le
 * corresponde en el navegador — el límite real de seguridad es `ComunidadInternalGuard`
 * en el backend: cada una de las llamadas que hacen los editores de abajo (crear, editar,
 * borrar, publicar) lo vuelve a chequear del lado del servidor en cada request.
 */
export const ComunidadAdminContent = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    checkComunidadAdminAccess().then((ok) => {
      setAllowed(ok);
      setChecking(false);
      if (!ok) navigate("/comunidad", { replace: true });
    });
  }, [navigate]);

  const handlePublish = async () => {
    // Lo que está escrito pero no se agregó ("Agregar edición" / "Agregar novedad" / "Guardar")
    // NO se publica: se avisa antes, para que no parezca que se publicó.
    const pending = hasPendingLocalDrafts();
    const message = pending
      ? "Ojo: tenés texto escrito que todavía no agregaste (falta tocar «Agregar edición», «Agregar novedad» o «Guardar»). Eso NO se va a publicar, pero queda guardado acá para que lo agregues después.\n\n¿Publicar igual lo que ya está agregado?"
      : "¿Publicar los cambios? Van a quedar visibles para todas las psicólogas al toque.";
    if (!window.confirm(message)) {
      return;
    }
    setPublishing(true);
    try {
      await comunidadAdapter.publish();
    } catch {
      toastUtils.error("No se pudo publicar. Probá de nuevo.");
    } finally {
      setPublishing(false);
    }
  };

  if (checking || !allowed) {
    return (
      <div className="flex min-h-full w-full items-center justify-center bg-[#E6DBFA]">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="min-h-full w-full bg-[#E6DBFA]">
      <div className="mx-auto flex max-w-[1000px] flex-col gap-6 px-8 py-10">

        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate("/comunidad")}
            className="flex items-center gap-1.5 text-sm font-semibold text-[#665090]"
          >
            <ArrowLeft size={16} />
            Volver a Comunidad
          </button>
          <Button onClick={handlePublish} disabled={publishing} className="bg-[#665090] hover:bg-[#665090]/90">
            <UploadCloud size={16} className="mr-1.5" />
            {publishing ? "Publicando…" : "Publicar cambios"}
          </Button>
        </div>

        <div>
          <h1 className="m-0 text-2xl font-extrabold text-[#2D2049]">Editar contenido de Comunidad</h1>
          <p className="m-0 mt-1 text-sm text-gray-500">
            Los cambios que hagas acá quedan como borrador — nadie más los ve hasta que apretes &ldquo;Publicar cambios&rdquo;.
          </p>
        </div>

        {/* Sin `key` que cambie al publicar: publicar no toca los borradores, y volver a montar
            los editores borraba lo que se estaba escribiendo sin haberlo agregado. */}
        <NewsFeedEditor feed="psi_platform" title="Novedades Psi Platform" />
        <NewsFeedEditor feed="institutional" title="Novedades institucionales" />
        <NewsletterEditionsEditor />
        <CalendarEventsEditor />

        <div className="h-px bg-[#D8C9F0]" />

        <div>
          <h2 className="m-0 text-xl font-extrabold text-[#2D2049]">Formaciones</h2>
          <p className="m-0 mt-1 text-sm text-gray-500">
            Esto no es borrador: los inscriptos se registran solos y la asistencia que subas impacta al toque.
          </p>
        </div>
        <FormacionInscriptosPanel />

        <div className="h-px bg-[#D8C9F0]" />

        <div>
          <h2 className="m-0 text-xl font-extrabold text-[#2D2049]">Voces de la Red</h2>
          <p className="m-0 mt-1 text-sm text-gray-500">
            Las respuestas a la pregunta de cada edición del newsletter. Llegan solas, no hace falta publicar.
          </p>
        </div>
        <VocesDeLaRedPanel />

      </div>
    </div>
  );
};

export default ComunidadAdminContent;
